import React, { useEffect, useState, useRef } from "react";
import { compose } from "redux";
import classNames from "classnames";
import OutsideClickHandler from 'react-outside-click-handler';
import Split from 'react-split'
import DropdownList from 'shared/components/DropdownList';
import DisplaySolutionPopupButton from 'shared/components/DisplaySolutionPopupButton';
import {Controlled as CodeMirror} from 'react-codemirror2';

import connectActions from "fantastic/lib/connectActions";
import actions from "fantastic/actions";

require('codemirror/mode/xml/xml');
require('codemirror/mode/javascript/javascript');
require('codemirror/mode/htmlmixed/htmlmixed');
require('codemirror/addon/selection/active-line');

const beautify = require('js-beautify').html;

const LinkText = () => <><i className="fi fi-download" title={ i18n.t('import') }/><span className="fantastic-html-editor__span-text">{ i18n.t('import') }</span></>;

const HtmlEditor = ({
  id,
  value,
  size,
  done,
  dir,
  resize,
  toggleDir,
  update,
  adminMode
}) => {
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const scrollRef = useRef(null);
  const divRef = useRef(null);

  const fileInputRef = useRef(null);
  const instance = useRef(null);

  const klass = classNames("fantastic-html-editor", {
    "fantastic-html-editor--down": dir === 'bottom',
    "fantastic-html-editor--left": dir === 'left'
  });

  useEffect(() => {
    if(!!id) setOpen(true);

    const timer = setTimeout(() => {
      if(!!!id) {
        setOpen(false);
      } else {
        instance.current.focus()
      }
    }, !!id ? 0 : 200);

    return () => clearTimeout(timer);
  }, [id]);

  const handleClose = () => {
    !blocked && done(id);
  }

  const handleToggle = () => {
    toggleDir();
  }

  const handleBeautifier = () => {
    update(beautify(value, { indent_size: 2, inline: [] }));
  }

  if(!open) return null;

  const splitAttributes =  {
    gutterSize: 10,
    gutterAlign: 'center',
    minSize: dir === 'bottom' ? 10 : 300,
    sizes: dir === 'bottom' ? [100 - size, size] : [size, 100 - size],
    direction: dir === 'bottom' ? 'vertical' : 'horizontal',
    onDragEnd: (sizes) => { resize(sizes[dir === 'bottom' ? 1 : 0]) }
  };

  const viewLeftClass = classNames("btn", "btn-link", "btn-xs",{
    "fantastic-html-editor--down": dir === 'bottom',
    "active": dir === 'left'
  });
  const viewDownClass = classNames("btn", "btn-link", "btn-xs",{
    "active": dir === 'bottom'
  });

  const options = {
    theme: 'monokai',
    mode: 'htmlmixed',
    lineNumbers: true,
    lineWrapping: true,
    styleActiveLine: true
  }

  const changeFileImport = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.addEventListener('load', (e) => {
      const data = e.target.result;
      if(data && data.length) update(data);
    });

    reader.readAsBinaryString(file);
  }

  const callbackWarehouse = (image) => {
    const line = instance.current.getCursor().line;
    instance.current.replaceRange(
      `<img src="${image.url}" alt="${image.name}" />`,
      { line }
    );
  }

  const appendCode = (code) => {
    setBlocked(false);
    !!code && update(code);
  };

  const isHiddenPopupButton = i18n.locale === 'pl';

  const renderBody = () => (
    <div className="fantastic-html-editor__body">
      <div className="fantastic-html-editor__header">
        <div className="fantastic-html-editor__header-tools">
          <div className="fantastic-html-editor__header-left-tools">
            <DropdownList link={<LinkText />}>
              <a onClick={ () => fileInputRef.current.click() } className="btn btn-link btn-xs">
                <i className="fi fi-download" />
                <span>{ i18n.t('import_file') }</span>
                <input
                  type="file"
                  onChange={changeFileImport}
                  accept=".html,.htm,.txt,text/html"
                  ref={fileInputRef}
                />
              </a>
              <a onClick={ () => { setBlocked(true); window["openImportUrl"](appendCode)} } className="btn btn-link btn-xs">
                <i className="fa fa-globe" />
                <span>{ i18n.t('html_insert') }</span>
              </a>
              <a onClick={ () => { setBlocked(true); window["openImportRss"](appendCode)} } className="btn btn-link btn-xs">
                <i className="fi fi-rss" />
                <span>{ i18n.t('rss_insert') }</span>
              </a>
            </DropdownList>
            <a onClick={handleBeautifier} className="btn btn-link btn-xs">
              <i className="fi fi-code" title={ i18n.t('format') }/>
              <span className="fantastic-html-editor__span-text">{ i18n.t('format') }</span>
            </a>
            <a onClick={ () => warehouseLoader.show(callbackWarehouse)} className="btn btn-link btn-xs">
              <i className="fi fi-image-v2" title={ i18n.t('image_insert') }/>
              <span className="fantastic-html-editor__span-text">{ i18n.t('image_insert') }</span>
            </a>
          </div>
          <div className="fantastic-html-editor__btn-toggle">
            <div className="fantastic-html-editor__btn-toggle-wrapper">
              {!isHiddenPopupButton && <DisplaySolutionPopupButton onClick={ () => setBlocked(true) } onClose={ () => setBlocked(false) }/>}
              <div className="fantastic-html-editor__toggle-buttons">
                <a onClick={handleToggle} className={viewDownClass}>
                  <i className="fi fi-view-html-down"/>
                </a>
                <a onClick={handleToggle} className={viewLeftClass}>
                  <i className="fi fi-view-html-left"/>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="fantastic-html-editor__close">
          <a onClick={handleClose} className="btn btn-form btn-xs"><i className="fa fa-remove" /></a>
        </div>
      </div>
      <div className="fantastic-html-editor__content">
        <CodeMirror
          value={value}
          options={options}
          onBeforeChange={(editor, data, value) => { update(value) }}
          editorDidMount={ (editor) => instance.current = editor }
          cursor={{ line: 0, ch: 0, focus: true }}
        />
      </div>
    </div>
  );

  return (
    <OutsideClickHandler onOutsideClick={handleClose}>
      {dir === 'bottom' ? (
        <Split {...splitAttributes} className={klass}>
          <div ref={divRef}></div>
          {renderBody()}
        </Split>
      ) : (
        <Split {...splitAttributes} className={klass}>
          {renderBody()}
          <div></div>
        </Split>
      )}
    </OutsideClickHandler>
  );
};

export default compose(
  connectActions({
    update: actions.updateHtml,
    done: actions.editDoneId,
    resize: actions.resizeHtml,
    toggleDir: actions.toggleDirHtml
  })
)(HtmlEditor);
